<!-- src/pages/HomePage.vue -->
<template>
    <main class="home" aria-label="M.C Bridge of Hope home page">
        <!-- =====================================================
             HERO
        ====================================================== -->
        <section class="hero reveal-item" aria-labelledby="hero-title" @mousemove="handleHeroMove"
            @mouseleave="resetHeroMove">
            <div class="hero-orb hero-orb--one" aria-hidden="true"></div>

            <div class="hero-orb hero-orb--two" aria-hidden="true"></div>

            <div class="shell hero-shell">
                <div class="hero-copy">
                    <p class="eyebrow eyebrow--light">
                        M.C Bridge of Hope
                    </p>

                    <h1 id="hero-title" class="hero-title">
                        Connecting people,
                        <span>knowledge and opportunity.</span>
                    </h1>

                    <p class="hero-sub">
                        We create spaces where people can connect,
                        learn, grow and access meaningful opportunities
                        through community-led programs, partnerships
                        and practical support.
                    </p>

                    <div class="hero-actions">
                        <RouterLink class="btn btn--gold" to="/programs">
                            Discover The Bridge
                            <span aria-hidden="true">→</span>
                        </RouterLink>

                        <button class="btn btn--glass" type="button" @click="openDonatePopup(50)">
                            Donate
                        </button>
                    </div>

                    <div class="hero-principles">
                        <span>People</span>
                        <i></i>
                        <span>Knowledge</span>
                        <i></i>
                        <span>Opportunity</span>
                    </div>
                </div>

                <div class="hero-visual" :style="heroTransformStyle">
                    <img class="hero-img" :src="heroPhotoUrl" :alt="heroPhotoAlt" loading="eager" decoding="async"
                        @error="onImgError('hero')" />

                    <div class="hero-overlay" aria-hidden="true"></div>

                    <div class="hero-card hero-card--top">
                        <span class="hero-card__icon">
                            ✦
                        </span>

                        <div>
                            <small>
                                Community-led
                            </small>

                            <strong>
                                Creating meaningful connections
                            </strong>
                        </div>
                    </div>

                    <div class="hero-card hero-card--bottom">
                        <span class="hero-card__dot"></span>

                        <div>
                            <small>
                                Next event
                            </small>

                            <strong>
                                The Bridge · 18 September 2026
                            </strong>
                        </div>
                    </div>
                </div>
            </div>
        </section>


        <!-- =====================================================
             FEATURED EVENT
        ====================================================== -->
        <section class="event-section reveal-item" aria-labelledby="featured-event-title">
            <div class="shell">
                <div class="event-panel">
                    <div class="event-panel__content">
                        <p class="eyebrow">
                            Upcoming event
                        </p>

                        <p class="event-panel__mini">
                            M.C Bridge of Hope presents
                        </p>

                        <h2 id="featured-event-title">
                            The Bridge
                        </h2>

                        <p class="event-panel__tagline">
                            Where ambition meets opportunity.
                        </p>

                        <div class="event-panel__topics">
                            <span>Entrepreneurship</span>
                            <i></i>
                            <span>Mentorship</span>
                            <i></i>
                            <span>Networking</span>
                        </div>

                        <p class="event-panel__description">
                            A South Sudanese and Sudanese community-led
                            evening connecting young adults with
                            entrepreneurs, business owners and
                            professionals.
                        </p>

                        <div class="event-meta">
                            <div class="event-meta__item">
                                <span class="event-meta__icon" aria-hidden="true" v-html="icons.calendar"></span>

                                <div>
                                    <small>Date</small>
                                    <strong>
                                        Friday, 18 September 2026
                                    </strong>
                                </div>
                            </div>

                            <div class="event-meta__item">
                                <span class="event-meta__icon" aria-hidden="true" v-html="icons.clock"></span>

                                <div>
                                    <small>Time</small>
                                    <strong>
                                        6:00 PM – 8:45 PM
                                    </strong>

                                    <em>
                                        Doors open 5:30 PM
                                    </em>
                                </div>
                            </div>

                            <a class="event-meta__item event-meta__item--link" :href="googleMapsUrl" target="_blank"
                                rel="noopener noreferrer">
                                <span class="event-meta__icon" aria-hidden="true" v-html="icons.location"></span>

                                <div>
                                    <small>Location</small>
                                    <strong>
                                        Rouse Hill Community Centre
                                    </strong>

                                    <em>
                                        Open in Google Maps ↗
                                    </em>
                                </div>
                            </a>
                        </div>

                        <div class="event-panel__actions">
                            <a class="btn btn--gold" :href="registrationUrl" target="_blank" rel="noopener noreferrer">
                                Register now
                                <span aria-hidden="true">→</span>
                            </a>

                            <RouterLink class="btn btn--outline" to="/programs">
                                Event details
                            </RouterLink>
                        </div>

                        <p class="event-panel__note">
                            Limited spaces available. Registration
                            is required.
                        </p>
                    </div>

                    <button class="event-artwork" type="button" aria-label="View The Bridge event banner larger" @click="
                        openImage(
                            bridgeBanner,
                            'The Bridge — Where Ambition Meets Opportunity'
                        )
                        ">
                        <img :src="bridgeBanner" alt="The Bridge event banner" />

                        <span class="image-hint">
                            View larger
                        </span>
                    </button>
                </div>
            </div>
        </section>


        <!-- =====================================================
             MISSION
        ====================================================== -->
        <section class="mission reveal-item" aria-labelledby="mission-title">
            <div class="shell mission-shell">
                <div class="mission-copy">
                    <p class="eyebrow">
                        Our purpose
                    </p>

                    <h2 id="mission-title" class="section-title">
                        Building bridges between people and possibility.
                    </h2>

                    <p class="section-sub">
                        M.C Bridge of Hope exists to strengthen
                        communities by creating opportunities for
                        connection, support, learning and growth.
                    </p>

                    <div class="mission-list">
                        <article v-for="(item, index) in missionItems" :key="item.title" class="mission-item">
                            <span>
                                0{{ index + 1 }}
                            </span>

                            <div>
                                <h3>
                                    {{ item.title }}
                                </h3>

                                <p>
                                    {{ item.text }}
                                </p>
                            </div>
                        </article>
                    </div>

                    <RouterLink class="text-link" to="/about">
                        Learn more about M.C Bridge of Hope →
                    </RouterLink>
                </div>

                <div class="mission-image">
                    <img :src="missionPhotoUrl" alt="Community members connecting and supporting each other"
                        loading="lazy" decoding="async" />

                    <div class="mission-image__caption">
                        <span>✦</span>

                        <p>
                            Community begins with connection.
                        </p>
                    </div>
                </div>
            </div>
        </section>


        <!-- =====================================================
             WHAT WE DO
        ====================================================== -->
        <section class="programs reveal-item" aria-labelledby="programs-title">
            <div class="shell">
                <div class="section-head">
                    <div>
                        <p class="eyebrow">
                            What we do
                        </p>

                        <h2 id="programs-title" class="section-title">
                            Creating opportunities that strengthen community.
                        </h2>

                        <p class="section-sub">
                            Our work combines connection, practical support,
                            mentorship and community-led initiatives.
                        </p>
                    </div>

                    <RouterLink class="btn btn--soft" to="/programs">
                        Explore programs
                    </RouterLink>
                </div>

                <div class="program-grid">
                    <article v-for="program in programs" :key="program.title" class="program-card">
                        <span class="program-card__icon" aria-hidden="true">
                            {{ program.icon }}
                        </span>

                        <small>
                            {{ program.kicker }}
                        </small>

                        <h3>
                            {{ program.title }}
                        </h3>

                        <p>
                            {{ program.description }}
                        </p>

                        <RouterLink class="program-card__link" to="/programs">
                            Learn more →
                        </RouterLink>
                    </article>
                </div>
            </div>
        </section>


        <!-- =====================================================
             IMPACT
        ====================================================== -->
        <section class="impact-section reveal-item" aria-labelledby="impact-title">
            <div class="shell impact-shell">
                <div class="impact-copy">
                    <p class="eyebrow">
                        Your support
                    </p>

                    <h2 id="impact-title" class="section-title">
                        Turn generosity into practical action.
                    </h2>

                    <p class="section-sub">
                        Select an amount to see the kind of support
                        your contribution could help provide.
                    </p>

                    <div class="donation-control">
                        <div class="donation-head">
                            <span>
                                Donation amount
                            </span>

                            <strong>
                                ${{ donationAmount }}
                            </strong>
                        </div>

                        <input v-model.number="donationAmount" class="donation-range" type="range" min="20" max="1000"
                            step="10" aria-label="Donation amount" />

                        <div class="range-labels">
                            <span>$20</span>
                            <span>$1000</span>
                        </div>
                    </div>
                </div>

                <div class="impact-preview">
                    <div class="impact-preview__amount">
                        <span>
                            ${{ donationAmount }}
                        </span>
                    </div>

                    <p class="impact-preview__label">
                        Potential support
                    </p>

                    <h3>
                        {{ donationImpact.title }}
                    </h3>

                    <p>
                        {{ donationImpact.text }}
                    </p>

                    <button class="btn btn--gold" type="button" @click="openDonatePopup(donationAmount)">
                        Donate ${{ donationAmount }}
                        <span aria-hidden="true">→</span>
                    </button>
                </div>
            </div>
        </section>


        <!-- =====================================================
             GET INVOLVED
        ====================================================== -->
        <section class="cta reveal-item" aria-label="Get involved with M.C Bridge of Hope">
            <div class="shell">
                <div class="cta-panel">
                    <div class="cta-copy">
                        <p class="eyebrow eyebrow--light">
                            Get involved
                        </p>

                        <h2>
                            Be part of what we’re building.
                        </h2>

                        <p>
                            Whether you want to attend, volunteer,
                            partner, donate or simply connect,
                            there is a place for you.
                        </p>

                        <div class="cta-actions">
                            <RouterLink class="btn btn--gold" to="/contact">
                                Connect with us
                                <span aria-hidden="true">→</span>
                            </RouterLink>

                            <button class="btn btn--glass" type="button" @click="openDonatePopup(50)">
                                Donate
                            </button>
                        </div>
                    </div>

                    <div class="cta-values">
                        <div v-for="value in values" :key="value.title" class="cta-value">
                            <span>
                                {{ value.icon }}
                            </span>

                            <div>
                                <strong>
                                    {{ value.title }}
                                </strong>

                                <small>
                                    {{ value.text }}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>


        <!-- =====================================================
             COPYRIGHT
        ====================================================== -->
        <section class="credits">
            <div class="shell">
                <p>
                    Copyright © 2026 M.C Bridge of Hope
                </p>
            </div>
        </section>


        <!-- DONATION -->
        <DonatePopup v-model="showDonatePopup" :amount="popupDonationAmount" :frequency="popupFrequency"
            :create-session-url="`${apiBaseUrl}/marychan/donate`" />


        <!-- =====================================================
             IMAGE LIGHTBOX
        ====================================================== -->
        <Teleport to="body">
            <transition name="lightbox-fade">
                <div v-if="lightboxImage" class="home-lightbox" role="dialog" aria-modal="true"
                    aria-label="Image preview" @click.self="closeImage">
                    <div class="home-lightbox__content">
                        <button class="home-lightbox__close" type="button" aria-label="Close image" @click="closeImage">
                            ×
                        </button>

                        <img class="home-lightbox__image" :src="lightboxImage" :alt="lightboxAlt" />

                        <p v-if="lightboxAlt" class="home-lightbox__caption">
                            {{ lightboxAlt }}
                        </p>
                    </div>
                </div>
            </transition>
        </Teleport>
    </main>
</template>


<script>
import { useHead } from "@unhead/vue";
import DonatePopup from "@/components/DonatePopup.vue";

import bridgeBanner from "@/assets/Banner.png";


const ICONS = {
    calendar: `
        <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="3"
                y="5"
                width="18"
                height="16"
                rx="3"
                stroke="currentColor"
                stroke-width="1.8"
            />

            <path
                d="M7 3v4M17 3v4M3 10h18"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
            />
        </svg>
    `,

    clock: `
        <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                stroke-width="1.8"
            />

            <path
                d="M12 7v5l3.5 2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
        </svg>
    `,

    location: `
        <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M12 21s7-6.1 7-12A7 7 0 1 0 5 9c0 5.9 7 12 7 12Z"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linejoin="round"
            />

            <circle
                cx="12"
                cy="9"
                r="2.3"
                stroke="currentColor"
                stroke-width="1.8"
            />
        </svg>
    `,
};


export default {
    name: "HomePage",

    components: {
        DonatePopup,
    },


    setup() {
        useHead({
            title:
                "M.C Bridge of Hope | Connecting People, Knowledge & Opportunity",

            meta: [
                {
                    name: "description",
                    content:
                        "M.C Bridge of Hope creates community-led opportunities through practical support, mentorship, partnerships, events and initiatives including The Bridge.",
                },

                {
                    name: "keywords",
                    content:
                        "M.C Bridge of Hope, The Bridge event, community support Australia, mentorship, youth empowerment, entrepreneurship, networking, South Sudanese community, Sudanese community",
                },

                {
                    name: "author",
                    content:
                        "M.C Bridge of Hope",
                },

                {
                    name: "robots",
                    content:
                        "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
                },

                {
                    property:
                        "og:title",

                    content:
                        "M.C Bridge of Hope | Connecting People, Knowledge & Opportunity",
                },

                {
                    property:
                        "og:description",

                    content:
                        "Building stronger communities through connection, opportunity, mentorship and practical support.",
                },

                {
                    property:
                        "og:type",

                    content:
                        "website",
                },

                {
                    property:
                        "og:url",

                    content:
                        "https://marychan.org.au/",
                },

                {
                    property:
                        "og:image",

                    content:
                        "https://marychan.org.au/Banner.png",
                },

                {
                    property:
                        "og:image:alt",

                    content:
                        "The Bridge - Where Ambition Meets Opportunity",
                },

                {
                    property:
                        "og:site_name",

                    content:
                        "M.C Bridge of Hope",
                },

                {
                    property:
                        "og:locale",

                    content:
                        "en_AU",
                },

                {
                    name:
                        "twitter:card",

                    content:
                        "summary_large_image",
                },

                {
                    name:
                        "twitter:title",

                    content:
                        "M.C Bridge of Hope | Connecting People, Knowledge & Opportunity",
                },

                {
                    name:
                        "twitter:description",

                    content:
                        "Community-led programs, events, mentorship and practical support.",
                },

                {
                    name:
                        "twitter:image",

                    content:
                        "https://marychan.org.au/Banner.png",
                },
            ],

            link: [
                {
                    rel:
                        "canonical",

                    href:
                        "https://marychan.org.au/",
                },

                {
                    rel:
                        "alternate",

                    hrefLang:
                        "en-au",

                    href:
                        "https://marychan.org.au/",
                },

                {
                    rel:
                        "alternate",

                    hrefLang:
                        "x-default",

                    href:
                        "https://marychan.org.au/",
                },
            ],

            script: [
                {
                    type:
                        "application/ld+json",

                    children:
                        JSON.stringify({
                            "@context":
                                "https://schema.org",

                            "@graph": [
                                {
                                    "@type":
                                        "Organization",

                                    "@id":
                                        "https://marychan.org.au/#organization",

                                    name:
                                        "M.C Bridge of Hope",

                                    url:
                                        "https://marychan.org.au/",

                                    logo:
                                        "https://marychan.org.au/logo7.png",

                                    email:
                                        "Connect@marychan.org.au",

                                    description:
                                        "M.C Bridge of Hope creates community-led opportunities through practical support, connection, mentorship and partnerships.",

                                    areaServed: {
                                        "@type":
                                            "Country",

                                        name:
                                            "Australia",
                                    },

                                    sameAs: [
                                        "https://www.instagram.com/bridgeofhope.mc/",
                                        "https://www.facebook.com/profile.php?id=61594445814032",
                                        "https://www.linkedin.com/in/mc-bridge-of-hope-44aa23434/",
                                        "https://www.tiktok.com/@marychan.org",
                                    ],
                                },

                                {
                                    "@type":
                                        "WebSite",

                                    "@id":
                                        "https://marychan.org.au/#website",

                                    url:
                                        "https://marychan.org.au/",

                                    name:
                                        "M.C Bridge of Hope",

                                    inLanguage:
                                        "en-AU",

                                    publisher: {
                                        "@id":
                                            "https://marychan.org.au/#organization",
                                    },
                                },

                                {
                                    "@type":
                                        "Event",

                                    "@id":
                                        "https://marychan.org.au/programs#the-bridge",

                                    name:
                                        "The Bridge - Where Ambition Meets Opportunity",

                                    startDate:
                                        "2026-09-18T18:00:00+10:00",

                                    endDate:
                                        "2026-09-18T20:45:00+10:00",

                                    doorTime:
                                        "2026-09-18T17:30:00+10:00",

                                    eventAttendanceMode:
                                        "https://schema.org/OfflineEventAttendanceMode",

                                    eventStatus:
                                        "https://schema.org/EventScheduled",

                                    location: {
                                        "@type":
                                            "Place",

                                        name:
                                            "Rouse Hill Community Centre",

                                        address: {
                                            "@type":
                                                "PostalAddress",

                                            addressLocality:
                                                "Rouse Hill",

                                            addressRegion:
                                                "NSW",

                                            addressCountry:
                                                "AU",
                                        },
                                    },

                                    organizer: {
                                        "@id":
                                            "https://marychan.org.au/#organization",
                                    },

                                    offers: {
                                        "@type":
                                            "Offer",

                                        url:
                                            "https://forms.gle/Q7EMmSVPMBz1xCiZ7",

                                        availability:
                                            "https://schema.org/InStock",

                                        price:
                                            "0",

                                        priceCurrency:
                                            "AUD",
                                    },
                                },
                            ],
                        }),
                },
            ],
        });

        return {};
    },


    data() {
        return {
            bridgeBanner,

            icons:
                ICONS,

            registrationUrl:
                "https://forms.gle/Q7EMmSVPMBz1xCiZ7",

            googleMapsUrl:
                "https://www.google.com/maps/search/?api=1&query=Rouse+Hill+Community+Centre",

            showDonatePopup:
                false,

            popupDonationAmount:
                50,

            popupFrequency:
                "one_off",

            heroX:
                0,

            heroY:
                0,

            donationAmount:
                50,

            lightboxImage:
                "",

            lightboxAlt:
                "",

            heroPhotoUrl:
                "https://images.pexels.com/photos/10855491/pexels-photo-10855491.jpeg?auto=compress&cs=tinysrgb&w=1600&h=1200&dpr=1",

            missionPhotoUrl:
                "https://images.pexels.com/photos/33920052/pexels-photo-33920052.jpeg?auto=compress&cs=tinysrgb&w=1400&h=1200&dpr=1",

            heroPhotoAlt:
                "Community members connecting and supporting each other",

            missionItems: [
                {
                    title:
                        "Connection",

                    text:
                        "Creating spaces where people can meet, exchange ideas and build meaningful relationships.",
                },

                {
                    title:
                        "Opportunity",

                    text:
                        "Connecting people with mentorship, knowledge, resources and pathways for growth.",
                },

                {
                    title:
                        "Community support",

                    text:
                        "Working with individuals, families and partners to strengthen communities through practical action.",
                },
            ],

            programs: [
                {
                    icon:
                        "✦",

                    kicker:
                        "Featured",

                    title:
                        "The Bridge",

                    description:
                        "An entrepreneurship, mentorship and networking initiative connecting ambition with opportunity.",
                },

                {
                    icon:
                        "♡",

                    kicker:
                        "Community",

                    title:
                        "Support & Outreach",

                    description:
                        "Community-led initiatives focused on practical support, connection and shared responsibility.",
                },

                {
                    icon:
                        "●",

                    kicker:
                        "Growth",

                    title:
                        "Mentorship & Guidance",

                    description:
                        "Creating opportunities to learn from experience, build confidence and develop meaningful direction.",
                },
            ],

            values: [
                {
                    icon:
                        "01",

                    title:
                        "People",

                    text:
                        "Community comes first.",
                },

                {
                    icon:
                        "02",

                    title:
                        "Knowledge",

                    text:
                        "Learning creates opportunity.",
                },

                {
                    icon:
                        "03",

                    title:
                        "Opportunity",

                    text:
                        "Connection can change a path.",
                },
            ],
        };
    },


    computed: {
        apiBaseUrl() {
            return "https://my-app-two-1d26224db3b5.herokuapp.com";
        },


        heroTransformStyle() {
            return {
                "--hero-x":
                    `${this.heroX}px`,

                "--hero-y":
                    `${this.heroY}px`,
            };
        },


        donationImpact() {
            if (
                this.donationAmount <
                40
            ) {
                return {
                    title:
                        "Practical essentials",

                    text:
                        "Could help contribute towards transport, supplies or immediate practical needs.",
                };
            }

            if (
                this.donationAmount <
                250
            ) {
                return {
                    title:
                        "Community support",

                    text:
                        "Could help provide resources, outreach materials or assistance for individuals and families.",
                };
            }

            if (
                this.donationAmount <
                500
            ) {
                return {
                    title:
                        "Program delivery",

                    text:
                        "Could contribute towards community events, mentorship, workshops and local initiatives.",
                };
            }

            return {
                title:
                    "Sustained impact",

                text:
                    "Could help support larger community initiatives, partnerships and longer-term programs.",
            };
        },
    },


    methods: {
        openDonatePopup(
            amount =
                this.donationAmount,

            frequency =
                "one_off",
        ) {
            const cleanAmount =
                Number(amount);

            this.popupDonationAmount =
                Number.isFinite(
                    cleanAmount,
                )
                    ? Math.round(
                        cleanAmount,
                    )
                    : 50;

            this.popupFrequency =
                frequency;

            this.showDonatePopup =
                true;
        },


        handleHeroMove(event) {
            const rect =
                event.currentTarget.getBoundingClientRect();

            const x =
                event.clientX -
                rect.left;

            const y =
                event.clientY -
                rect.top;

            this.heroX =
                (x / rect.width -
                    0.5) *
                13;

            this.heroY =
                (y / rect.height -
                    0.5) *
                13;
        },


        resetHeroMove() {
            this.heroX = 0;
            this.heroY = 0;
        },


        openImage(
            src,
            alt = "",
        ) {
            this.lightboxImage =
                src;

            this.lightboxAlt =
                alt;

            if (
                typeof document !==
                "undefined"
            ) {
                document.body.style.overflow =
                    "hidden";
            }
        },


        closeImage() {
            this.lightboxImage =
                "";

            this.lightboxAlt =
                "";

            if (
                typeof document !==
                "undefined"
            ) {
                document.body.style.overflow =
                    "";
            }
        },


        handleKeydown(event) {
            if (
                event.key ===
                "Escape" &&
                this.lightboxImage
            ) {
                this.closeImage();
            }
        },


        setupRevealAnimations() {
            const elements =
                Array.from(
                    document.querySelectorAll(
                        ".reveal-item",
                    ),
                );

            if (
                !(
                    "IntersectionObserver" in
                    window
                )
            ) {
                elements.forEach(
                    (element) =>
                        element.classList.add(
                            "revealed",
                        ),
                );

                return;
            }

            const observer =
                new IntersectionObserver(
                    (
                        entries,
                    ) => {
                        entries.forEach(
                            (
                                entry,
                            ) => {
                                if (
                                    !entry.isIntersecting
                                ) {
                                    return;
                                }

                                entry.target.classList.add(
                                    "revealed",
                                );

                                observer.unobserve(
                                    entry.target,
                                );
                            },
                        );
                    },

                    {
                        threshold:
                            0.12,

                        rootMargin:
                            "0px 0px -40px 0px",
                    },
                );

            elements.forEach(
                (element) =>
                    observer.observe(
                        element,
                    ),
            );
        },


        onImgError(which) {
            if (
                which ===
                "hero"
            ) {
                this.heroPhotoUrl =
                    "https://images.pexels.com/photos/7551591/pexels-photo-7551591.jpeg?auto=compress&cs=tinysrgb&w=1600&h=1200&dpr=1";
            }
        },
    },


    mounted() {
        if (
            typeof window !==
            "undefined"
        ) {
            window.scrollTo(
                0,
                0,
            );

            window.addEventListener(
                "keydown",
                this.handleKeydown,
            );

            this.setupRevealAnimations();
        }
    },


    beforeUnmount() {
        if (
            typeof window !==
            "undefined"
        ) {
            window.removeEventListener(
                "keydown",
                this.handleKeydown,
            );
        }

        if (
            typeof document !==
            "undefined"
        ) {
            document.body.style.overflow =
                "";
        }
    },
};
</script>


<style scoped lang="scss">
.home {
    --navy-950: #02182b;
    --navy-900: #03233e;
    --navy-800: #063659;
    --navy-700: #0b4a70;

    --gold-600: #b87906;
    --gold-500: #d99a16;
    --gold-400: #edb335;
    --gold-300: #f4c862;

    --paper: #ffffff;
    --cream: #f8f5ee;
    --cream-soft: #fcfaf6;

    --ink: #0b223d;
    --muted: #667486;

    --line: rgba(7, 42, 73, 0.1);

    --radius-md: 18px;
    --radius-lg: 26px;
    --radius-xl: 38px;

    --shadow-sm:
        0 12px 32px rgba(2, 24, 43, 0.07);

    --shadow-md:
        0 22px 58px rgba(2, 24, 43, 0.12);

    --shadow-lg:
        0 36px 90px rgba(2, 24, 43, 0.18);

    min-height: 100%;
    overflow: hidden;
    color: var(--ink);
    background: #fff;

    * {
        box-sizing: border-box;
    }


    /* =====================================================
       SHARED
    ====================================================== */

    .shell {
        width:
            min(1180px,
                calc(100% - 32px));

        margin:
            0 auto;

        @media (min-width: 760px) {
            width:
                min(1180px,
                    calc(100% - 52px));
        }

        @media (min-width: 1180px) {
            width:
                min(1240px,
                    calc(100% - 72px));
        }
    }


    .reveal-item {
        opacity: 0;

        transform:
            translateY(25px);

        transition:
            opacity 650ms ease,
            transform 650ms ease;

        &.revealed {
            opacity: 1;

            transform:
                translateY(0);
        }
    }


    .eyebrow {
        margin:
            0 0 11px;

        color:
            var(--gold-600);

        font-size:
            11px;

        line-height:
            1.2;

        font-weight:
            950;

        letter-spacing:
            0.17em;

        text-transform:
            uppercase;

        &.eyebrow--light {
            color:
                var(--gold-300);
        }
    }


    .section-title {
        max-width:
            820px;

        margin:
            0 0 15px;

        color:
            var(--navy-900);

        font-size:
            clamp(34px,
                4.7vw,
                60px);

        line-height:
            1;

        font-weight:
            950;

        letter-spacing:
            -0.045em;
    }


    .section-sub {
        max-width:
            66ch;

        margin: 0;

        color:
            var(--muted);

        font-size:
            16px;

        line-height:
            1.8;
    }


    .section-head {
        display: grid;

        gap: 22px;

        align-items:
            end;

        margin-bottom:
            30px;

        @media (min-width: 820px) {
            grid-template-columns:
                minmax(0,
                    1fr) auto;
        }
    }


    .btn {
        appearance: none;

        display:
            inline-flex;

        align-items:
            center;

        justify-content:
            center;

        gap:
            10px;

        min-height:
            50px;

        padding:
            13px 21px;

        border:
            1px solid transparent;

        border-radius:
            999px;

        cursor:
            pointer;

        font-family:
            inherit;

        font-size:
            14px;

        line-height:
            1;

        font-weight:
            900;

        text-decoration:
            none;

        transition:
            transform 180ms ease,
            box-shadow 180ms ease,
            background 180ms ease,
            border-color 180ms ease;

        >span {
            font-size:
                18px;

            transition:
                transform 180ms ease;
        }

        &:hover {
            transform:
                translateY(-2px);

            >span {
                transform:
                    translateX(3px);
            }
        }

        &.btn--gold {
            color:
                var(--navy-950);

            background:
                linear-gradient(135deg,
                    #f8cf70,
                    var(--gold-400),
                    var(--gold-500));

            box-shadow:
                0 17px 36px rgba(217,
                    154,
                    22,
                    0.25);
        }

        &.btn--glass {
            color:
                #fff;

            border-color:
                rgba(255,
                    255,
                    255,
                    0.2);

            background:
                rgba(255,
                    255,
                    255,
                    0.08);

            backdrop-filter:
                blur(13px);

            &:hover {
                background:
                    rgba(255,
                        255,
                        255,
                        0.14);
            }
        }

        &.btn--outline {
            color:
                var(--navy-900);

            border-color:
                var(--line);

            background:
                #fff;

            box-shadow:
                var(--shadow-sm);
        }

        &.btn--soft {
            color:
                var(--navy-900);

            border-color:
                var(--line);

            background:
                var(--cream-soft);

            box-shadow:
                var(--shadow-sm);
        }

        @media (max-width: 560px) {
            width:
                100%;
        }
    }


    .text-link {
        color:
            var(--gold-600);

        font-size:
            14px;

        font-weight:
            900;

        text-decoration:
            none;

        &:hover {
            text-decoration:
                underline;
        }
    }


    /* =====================================================
       HERO
    ====================================================== */

    .hero {
        position:
            relative;

        isolation:
            isolate;

        overflow:
            hidden;

        padding:
            82px 0 76px;

        color:
            #fff;

        background:
            radial-gradient(780px 500px at 88% 0%,
                rgba(217,
                    154,
                    22,
                    0.2),
                transparent 62%),
            radial-gradient(620px 420px at 0% 90%,
                rgba(11,
                    74,
                    112,
                    0.35),
                transparent 70%),
            linear-gradient(135deg,
                var(--navy-950),
                var(--navy-900),
                #073a5e);

        @media (max-width: 720px) {
            padding:
                52px 0 48px;
        }
    }


    .hero-orb {
        position:
            absolute;

        border-radius:
            50%;

        pointer-events:
            none;

        filter:
            blur(5px);
    }


    .hero-orb--one {
        top:
            -180px;

        left:
            -150px;

        width:
            420px;

        height:
            420px;

        background:
            rgba(217,
                154,
                22,
                0.09);
    }


    .hero-orb--two {
        right:
            -200px;

        bottom:
            -250px;

        width:
            540px;

        height:
            540px;

        background:
            rgba(255,
                255,
                255,
                0.045);
    }


    .hero-shell {
        position:
            relative;

        z-index:
            2;

        display:
            grid;

        gap:
            46px;

        align-items:
            center;

        @media (min-width: 960px) {
            grid-template-columns:
                minmax(0,
                    0.9fr) minmax(480px,
                    1.1fr);
        }
    }


    .hero-copy {
        max-width:
            680px;
    }


    .hero-title {
        margin:
            0;

        color:
            #fff;

        font-size:
            clamp(46px,
                6.4vw,
                78px);

        line-height:
            0.97;

        font-weight:
            950;

        letter-spacing:
            -0.05em;

        span {
            display:
                block;

            margin-top:
                7px;

            color:
                var(--gold-300);
        }
    }


    .hero-sub {
        max-width:
            62ch;

        margin:
            24px 0 0;

        color:
            rgba(255,
                255,
                255,
                0.72);

        font-size:
            16px;

        line-height:
            1.82;
    }


    .hero-actions {
        display:
            flex;

        flex-wrap:
            wrap;

        gap:
            12px;

        margin-top:
            28px;

        @media (max-width: 560px) {
            display:
                grid;
        }
    }


    .hero-principles {
        display:
            flex;

        flex-wrap:
            wrap;

        align-items:
            center;

        gap:
            10px;

        margin-top:
            30px;

        color:
            rgba(255,
                255,
                255,
                0.62);

        font-size:
            11px;

        font-weight:
            850;

        letter-spacing:
            0.12em;

        text-transform:
            uppercase;

        i {
            width:
                5px;

            height:
                5px;

            border-radius:
                50%;

            background:
                var(--gold-400);
        }
    }


    .hero-visual {
        --hero-x:
            0px;

        --hero-y:
            0px;

        position:
            relative;

        min-height:
            430px;

        overflow:
            hidden;

        border:
            1px solid rgba(255,
                255,
                255,
                0.17);

        border-radius:
            var(--radius-xl);

        background:
            rgba(255,
                255,
                255,
                0.06);

        box-shadow:
            0 40px 95px rgba(0,
                0,
                0,
                0.32);

        transform:
            perspective(1000px) rotateY(calc(var(--hero-x) * 0.1deg)) rotateX(calc(var(--hero-y) * -0.1deg));

        transition:
            transform 180ms ease;

        @media (min-width: 900px) {
            min-height:
                560px;
        }

        @media (max-width: 560px) {
            min-height:
                380px;
        }
    }


    .hero-img {
        display:
            block;

        position:
            absolute;

        inset: 0;

        width:
            100%;

        height:
            100%;

        object-fit:
            cover;
    }


    .hero-overlay {
        position:
            absolute;

        inset: 0;

        background:
            linear-gradient(180deg,
                rgba(2,
                    24,
                    43,
                    0.03),
                rgba(2,
                    24,
                    43,
                    0.58));

        pointer-events:
            none;
    }


    .hero-card {
        position:
            absolute;

        z-index:
            3;

        display:
            flex;

        gap:
            11px;

        align-items:
            center;

        max-width:
            270px;

        padding:
            12px 14px;

        border:
            1px solid rgba(255,
                255,
                255,
                0.62);

        border-radius:
            18px;

        color:
            var(--navy-900);

        background:
            rgba(255,
                255,
                255,
                0.9);

        backdrop-filter:
            blur(14px);

        box-shadow:
            0 18px 42px rgba(2,
                24,
                43,
                0.22);

        small,
        strong {
            display:
                block;
        }

        small {
            margin-bottom:
                3px;

            color:
                var(--muted);

            font-size:
                9px;

            font-weight:
                900;

            letter-spacing:
                0.1em;

            text-transform:
                uppercase;
        }

        strong {
            font-size:
                12px;

            line-height:
                1.35;

            font-weight:
                900;
        }
    }


    .hero-card--top {
        top:
            18px;

        left:
            18px;
    }


    .hero-card--bottom {
        right:
            18px;

        bottom:
            18px;
    }


    .hero-card__icon {
        display:
            grid;

        width:
            34px;

        height:
            34px;

        place-items:
            center;

        flex:
            0 0 auto;

        border-radius:
            12px;

        color:
            var(--gold-400);

        background:
            var(--navy-900);
    }


    .hero-card__dot {
        width:
            11px;

        height:
            11px;

        flex:
            0 0 auto;

        border-radius:
            50%;

        background:
            var(--gold-500);

        box-shadow:
            0 0 0 7px rgba(217,
                154,
                22,
                0.15);
    }


    /* =====================================================
       EVENT
    ====================================================== */

    .event-section {
        padding:
            82px 0;

        background:
            linear-gradient(180deg,
                #f8f5ef,
                #fff);

        @media (max-width: 720px) {
            padding:
                54px 0;
        }
    }


    .event-panel {
        overflow:
            hidden;

        display:
            grid;

        border:
            1px solid var(--line);

        border-radius:
            40px;

        background:
            #fff;

        box-shadow:
            var(--shadow-lg);

        @media (min-width: 980px) {
            grid-template-columns:
                minmax(0,
                    1fr) 520px;
        }
    }


    .event-panel__content {
        padding:
            clamp(28px,
                5vw,
                54px);

        background:
            radial-gradient(500px 250px at 100% 0%,
                rgba(217,
                    154,
                    22,
                    0.08),
                transparent 70%);
    }


    .event-panel__mini {
        margin:
            0 0 8px;

        color:
            var(--muted);

        font-size:
            12px;

        font-weight:
            800;
    }


    .event-panel h2 {
        margin: 0;

        color:
            var(--navy-900);

        font-family:
            Georgia,
            "Times New Roman",
            serif;

        font-size:
            clamp(58px,
                7vw,
                92px);

        line-height:
            0.84;

        font-weight:
            500;

        letter-spacing:
            -0.05em;

        text-transform:
            uppercase;
    }


    .event-panel__tagline {
        margin:
            20px 0 0;

        color:
            var(--gold-600);

        font-size:
            clamp(20px,
                2.4vw,
                28px);

        font-weight:
            950;

        text-transform:
            uppercase;
    }


    .event-panel__topics {
        display:
            flex;

        flex-wrap:
            wrap;

        align-items:
            center;

        gap:
            10px;

        margin-top:
            18px;

        color:
            var(--navy-900);

        font-size:
            11px;

        font-weight:
            900;

        letter-spacing:
            0.08em;

        text-transform:
            uppercase;

        i {
            width:
                5px;

            height:
                5px;

            border-radius:
                50%;

            background:
                var(--gold-500);
        }
    }


    .event-panel__description {
        max-width:
            62ch;

        margin:
            22px 0 0;

        color:
            var(--muted);

        font-size:
            15px;

        line-height:
            1.75;
    }


    .event-meta {
        display:
            grid;

        gap:
            9px;

        margin-top:
            26px;
    }


    .event-meta__item {
        display:
            flex;

        align-items:
            center;

        gap:
            13px;

        padding:
            12px 13px;

        border:
            1px solid var(--line);

        border-radius:
            17px;

        color:
            inherit;

        background:
            var(--cream-soft);

        text-decoration:
            none;

        transition:
            transform 160ms ease,
            border-color 160ms ease;

        &:hover {
            transform:
                translateX(3px);

            border-color:
                rgba(217,
                    154,
                    22,
                    0.3);
        }

        small,
        strong,
        em {
            display:
                block;
        }

        small {
            margin-bottom:
                2px;

            color:
                var(--muted);

            font-size:
                9px;

            font-weight:
                900;

            letter-spacing:
                0.11em;

            text-transform:
                uppercase;
        }

        strong {
            color:
                var(--navy-900);

            font-size:
                13px;

            line-height:
                1.35;

            font-weight:
                900;
        }

        em {
            margin-top:
                2px;

            color:
                var(--gold-600);

            font-size:
                10px;

            font-style:
                normal;

            font-weight:
                800;
        }
    }


    .event-meta__icon {
        display:
            grid;

        width:
            44px;

        height:
            44px;

        place-items:
            center;

        flex:
            0 0 auto;

        border-radius:
            14px;

        color:
            var(--gold-400);

        background:
            var(--navy-900);
    }


    .event-panel__actions {
        display:
            flex;

        flex-wrap:
            wrap;

        gap:
            11px;

        margin-top:
            27px;

        @media (max-width: 560px) {
            display:
                grid;
        }
    }


    .event-panel__note {
        margin:
            12px 0 0;

        color:
            var(--muted);

        font-size:
            11px;

        font-weight:
            700;
    }


    .event-artwork {
        position:
            relative;

        overflow:
            hidden;

        width:
            100%;

        min-height:
            500px;

        padding: 0;

        border: 0;

        background:
            var(--navy-900);

        cursor:
            zoom-in;

        img {
            display:
                block;

            width:
                100%;

            height:
                100%;

            object-fit:
                cover;

            transition:
                transform 350ms ease;
        }

        &:hover {
            img {
                transform:
                    scale(1.025);
            }

            .image-hint {
                opacity: 1;

                transform:
                    translateY(0);
            }
        }

        @media (max-width: 979px) {
            min-height:
                auto;

            img {
                height:
                    auto;
            }
        }
    }


    .image-hint {
        position:
            absolute;

        right:
            16px;

        bottom:
            16px;

        padding:
            9px 13px;

        border:
            1px solid rgba(255,
                255,
                255,
                0.2);

        border-radius:
            999px;

        color:
            #fff;

        background:
            rgba(2,
                24,
                43,
                0.78);

        backdrop-filter:
            blur(12px);

        font-size:
            11px;

        font-weight:
            850;

        opacity:
            0;

        transform:
            translateY(6px);

        transition:
            opacity 180ms ease,
            transform 180ms ease;

        @media (max-width: 720px) {
            opacity: 1;

            transform:
                none;
        }
    }


    /* =====================================================
       MISSION
    ====================================================== */

    .mission {
        padding:
            84px 0;

        background:
            #fff;

        @media (max-width: 720px) {
            padding:
                56px 0;
        }
    }


    .mission-shell {
        display:
            grid;

        gap:
            48px;

        align-items:
            center;

        @media (min-width: 940px) {
            grid-template-columns:
                minmax(0,
                    1fr) 470px;
        }
    }


    .mission-list {
        display:
            grid;

        gap:
            10px;

        margin:
            28px 0;
    }


    .mission-item {
        display:
            grid;

        grid-template-columns:
            42px minmax(0,
                1fr);

        gap:
            14px;

        align-items:
            start;

        padding:
            15px;

        border:
            1px solid var(--line);

        border-radius:
            18px;

        background:
            var(--cream-soft);

        transition:
            transform 180ms ease,
            border-color 180ms ease;

        &:hover {
            transform:
                translateX(4px);

            border-color:
                rgba(217,
                    154,
                    22,
                    0.28);
        }

        >span {
            display:
                grid;

            width:
                40px;

            height:
                40px;

            place-items:
                center;

            border-radius:
                14px;

            color:
                #fff;

            background:
                linear-gradient(145deg,
                    var(--navy-800),
                    var(--gold-500));

            font-size:
                11px;

            font-weight:
                900;
        }

        h3 {
            margin:
                0 0 5px;

            color:
                var(--navy-900);

            font-size:
                16px;

            font-weight:
                950;
        }

        p {
            margin: 0;

            color:
                var(--muted);

            font-size:
                13px;

            line-height:
                1.6;
        }
    }


    .mission-image {
        position:
            relative;

        overflow:
            hidden;

        min-height:
            510px;

        border-radius:
            var(--radius-xl);

        background:
            var(--cream);

        box-shadow:
            var(--shadow-lg);

        img {
            position:
                absolute;

            inset: 0;

            width:
                100%;

            height:
                100%;

            object-fit:
                cover;
        }

        @media (max-width: 700px) {
            min-height:
                420px;
        }
    }


    .mission-image__caption {
        position:
            absolute;

        left:
            16px;

        right:
            16px;

        bottom:
            16px;

        display:
            flex;

        align-items:
            center;

        gap:
            10px;

        padding:
            13px 15px;

        border:
            1px solid rgba(255,
                255,
                255,
                0.65);

        border-radius:
            18px;

        color:
            var(--navy-900);

        background:
            rgba(255,
                255,
                255,
                0.9);

        backdrop-filter:
            blur(14px);

        span {
            color:
                var(--gold-600);

            font-size:
                20px;
        }

        p {
            margin: 0;

            font-size:
                13px;

            font-weight:
                900;
        }
    }


    /* =====================================================
       PROGRAMS
    ====================================================== */

    .programs {
        padding:
            84px 0;

        background:
            linear-gradient(180deg,
                #f5f7f9,
                #f8fafb);

        @media (max-width: 720px) {
            padding:
                56px 0;
        }
    }


    .program-grid {
        display:
            grid;

        gap:
            17px;

        @media (min-width: 760px) {
            grid-template-columns:
                repeat(3,
                    minmax(0,
                        1fr));
        }
    }


    .program-card {
        position:
            relative;

        overflow:
            hidden;

        padding:
            27px;

        border:
            1px solid var(--line);

        border-radius:
            var(--radius-lg);

        background:
            #fff;

        box-shadow:
            var(--shadow-sm);

        transition:
            transform 190ms ease,
            box-shadow 190ms ease,
            border-color 190ms ease;

        &:hover {
            transform:
                translateY(-6px);

            border-color:
                rgba(217,
                    154,
                    22,
                    0.27);

            box-shadow:
                var(--shadow-md);
        }

        >small {
            display:
                block;

            margin-top:
                18px;

            color:
                var(--gold-600);

            font-size:
                10px;

            font-weight:
                900;

            letter-spacing:
                0.13em;

            text-transform:
                uppercase;
        }

        h3 {
            margin:
                8px 0 10px;

            color:
                var(--navy-900);

            font-size:
                24px;

            line-height:
                1.08;

            font-weight:
                950;
        }

        p {
            margin:
                0 0 20px;

            color:
                var(--muted);

            line-height:
                1.7;
        }
    }


    .program-card__icon {
        display:
            grid;

        width:
            58px;

        height:
            58px;

        place-items:
            center;

        border-radius:
            19px;

        color:
            var(--gold-400);

        background:
            var(--navy-900);

        font-size:
            21px;

        font-weight:
            900;
    }


    .program-card__link {
        color:
            var(--gold-600);

        font-size:
            13px;

        font-weight:
            900;

        text-decoration:
            none;
    }


    /* =====================================================
       IMPACT
    ====================================================== */

    .impact-section {
        padding:
            84px 0;

        background:
            radial-gradient(680px 340px at 5% 0%,
                rgba(217,
                    154,
                    22,
                    0.12),
                transparent 68%),
            linear-gradient(135deg,
                #fbf6ed,
                #eef5f8);

        @media (max-width: 720px) {
            padding:
                56px 0;
        }
    }


    .impact-shell {
        display:
            grid;

        gap:
            42px;

        align-items:
            center;

        @media (min-width: 900px) {
            grid-template-columns:
                minmax(0,
                    1fr) 430px;
        }
    }


    .donation-control {
        max-width:
            600px;

        margin-top:
            26px;

        padding:
            18px;

        border:
            1px solid var(--line);

        border-radius:
            19px;

        background:
            rgba(255,
                255,
                255,
                0.8);

        box-shadow:
            var(--shadow-sm);
    }


    .donation-head {
        display:
            flex;

        justify-content:
            space-between;

        align-items:
            center;

        gap:
            12px;

        margin-bottom:
            15px;

        color:
            var(--muted);

        font-size:
            13px;

        font-weight:
            850;

        strong {
            color:
                var(--navy-900);

            font-size:
                23px;

            font-weight:
                950;
        }
    }


    .donation-range {
        width:
            100%;

        accent-color:
            var(--gold-500);
    }


    .range-labels {
        display:
            flex;

        justify-content:
            space-between;

        margin-top:
            8px;

        color:
            var(--muted);

        font-size:
            10px;

        font-weight:
            750;
    }


    .impact-preview {
        padding:
            31px;

        border:
            1px solid rgba(255,
                255,
                255,
                0.12);

        border-radius:
            var(--radius-xl);

        color:
            #fff;

        background:
            radial-gradient(circle at top right,
                rgba(217,
                    154,
                    22,
                    0.2),
                transparent 45%),
            linear-gradient(145deg,
                var(--navy-800),
                var(--navy-950));

        box-shadow:
            var(--shadow-lg);

        h3 {
            margin:
                12px 0 10px;

            color:
                #fff;

            font-size:
                29px;

            line-height:
                1.05;

            font-weight:
                950;
        }

        >p:not(.impact-preview__label) {
            margin:
                0 0 23px;

            color:
                rgba(255,
                    255,
                    255,
                    0.7);

            line-height:
                1.7;
        }
    }


    .impact-preview__amount {
        display:
            grid;

        width:
            88px;

        height:
            88px;

        place-items:
            center;

        border:
            1px solid rgba(255,
                255,
                255,
                0.15);

        border-radius:
            50%;

        color:
            var(--navy-950);

        background:
            linear-gradient(145deg,
                var(--gold-300),
                var(--gold-500));

        box-shadow:
            0 16px 38px rgba(0,
                0,
                0,
                0.18);

        span {
            font-size:
                20px;

            font-weight:
                950;
        }
    }


    .impact-preview__label {
        margin:
            21px 0 0;

        color:
            var(--gold-300);

        font-size:
            10px;

        font-weight:
            950;

        letter-spacing:
            0.14em;

        text-transform:
            uppercase;
    }


    /* =====================================================
       CTA
    ====================================================== */

    .cta {
        padding:
            84px 0;

        background:
            #fff;

        @media (max-width: 720px) {
            padding:
                56px 0;
        }
    }


    .cta-panel {
        position:
            relative;

        overflow:
            hidden;

        display:
            grid;

        gap:
            40px;

        padding:
            clamp(30px,
                5vw,
                56px);

        border-radius:
            40px;

        color:
            #fff;

        background:
            radial-gradient(640px 320px at 100% 0%,
                rgba(217,
                    154,
                    22,
                    0.24),
                transparent 64%),
            linear-gradient(135deg,
                var(--navy-800),
                var(--navy-950));

        box-shadow:
            var(--shadow-lg);

        @media (min-width: 900px) {
            grid-template-columns:
                minmax(0,
                    1fr) 390px;

            align-items:
                center;
        }
    }


    .cta-copy {
        h2 {
            max-width:
                700px;

            margin: 0;

            color:
                #fff;

            font-size:
                clamp(40px,
                    5vw,
                    66px);

            line-height:
                0.98;

            font-weight:
                950;

            letter-spacing:
                -0.045em;
        }

        >p {
            max-width:
                620px;

            margin:
                18px 0 0;

            color:
                rgba(255,
                    255,
                    255,
                    0.68);

            line-height:
                1.76;
        }
    }


    .cta-actions {
        display:
            flex;

        flex-wrap:
            wrap;

        gap:
            11px;

        margin-top:
            25px;

        @media (max-width: 560px) {
            display:
                grid;
        }
    }


    .cta-values {
        display:
            grid;

        gap:
            10px;
    }


    .cta-value {
        display:
            flex;

        gap:
            12px;

        align-items:
            center;

        padding:
            13px 14px;

        border:
            1px solid rgba(255,
                255,
                255,
                0.11);

        border-radius:
            17px;

        background:
            rgba(255,
                255,
                255,
                0.06);

        span {
            display:
                grid;

            width:
                39px;

            height:
                39px;

            place-items:
                center;

            flex:
                0 0 auto;

            border-radius:
                13px;

            color:
                var(--navy-950);

            background:
                var(--gold-400);

            font-size:
                10px;

            font-weight:
                950;
        }

        strong,
        small {
            display:
                block;
        }

        strong {
            color:
                #fff;

            font-size:
                13px;

            font-weight:
                900;
        }

        small {
            margin-top:
                2px;

            color:
                rgba(255,
                    255,
                    255,
                    0.54);

            font-size:
                11px;
        }
    }


    /* =====================================================
       COPYRIGHT
    ====================================================== */

    .credits {
        padding:
            22px 0;

        border-top:
            1px solid var(--line);

        background:
            var(--cream-soft);

        p {
            margin: 0;

            color:
                var(--muted);

            font-size:
                11px;

            text-align:
                center;
        }
    }


    @media (prefers-reduced-motion: reduce) {

        *,
        *::before,
        *::after {
            animation-duration:
                0.001ms !important;

            animation-iteration-count:
                1 !important;

            transition-duration:
                0.001ms !important;

            scroll-behavior:
                auto !important;
        }
    }
}


/* =========================================================
   LIGHTBOX — TELEPORTED OUTSIDE SCOPED ROOT
========================================================= */

:global(.home-lightbox) {
    position:
        fixed;

    inset: 0;

    z-index:
        9999;

    display:
        grid;

    place-items:
        center;

    padding:
        28px;

    background:
        rgba(1,
            13,
            24,
            0.94);

    backdrop-filter:
        blur(12px);
}


:global(.home-lightbox__content) {
    position:
        relative;

    display:
        grid;

    place-items:
        center;

    width:
        min(1200px,
            100%);

    max-height:
        calc(100vh - 56px);
}


:global(.home-lightbox__image) {
    display:
        block;

    max-width:
        100%;

    max-height:
        calc(100vh - 90px);

    object-fit:
        contain;

    border-radius:
        18px;

    box-shadow:
        0 42px 110px rgba(0,
            0,
            0,
            0.58);
}


:global(.home-lightbox__close) {
    position:
        absolute;

    top:
        -18px;

    right:
        -18px;

    z-index:
        3;

    display:
        grid;

    width:
        46px;

    height:
        46px;

    place-items:
        center;

    border:
        1px solid rgba(255,
            255,
            255,
            0.2);

    border-radius:
        50%;

    color:
        #fff;

    background:
        rgba(2,
            24,
            43,
            0.94);

    cursor:
        pointer;

    font-size:
        24px;

    line-height:
        1;

    box-shadow:
        0 12px 30px rgba(0,
            0,
            0,
            0.3);
}


:global(.home-lightbox__caption) {
    margin:
        12px 0 0;

    color:
        rgba(255,
            255,
            255,
            0.7);

    font-size:
        12px;

    font-weight:
        700;

    text-align:
        center;
}


:global(.lightbox-fade-enter-active),
:global(.lightbox-fade-leave-active) {
    transition:
        opacity 180ms ease;
}


:global(.lightbox-fade-enter-from),
:global(.lightbox-fade-leave-to) {
    opacity: 0;
}
</style>